/**
 * The onboarding wizard's four step panels (phase 7b item 3), each
 * presentational: what the step makes, a form for it (local state), and what
 * it made once done. The container (`OnboardingView`) does the requests —
 * every one idempotent (a client-chosen id, or adopting what exists) — and
 * saves the progress.
 *
 *   ShowStyleStep  a show style from the template (D32: piece types, layers)
 *   StudioStep     a studio from the template (devices, mappings) naming it, and the pair's judgement
 *   ServerStep     add a server (a site, its enrolment code and the `docker run` line) — or launch a cloud production
 *   RundownStep    a first rundown, seeded with a demo show for the template, and open it
 */
import { CloudIcon, CopyIcon, ServerIcon } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Button } from '../ui/button.js';
import { Field, FieldDescription, FieldError } from '../ui/field.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';

function Done({ children }: { readonly children: ReactNode }) {
  return (
    <p className="rounded-md border bg-muted p-3 text-sm" data-testid="onboarding-step-done">
      {children}
    </p>
  );
}

// ─── 1. show style ───

export type ShowStyleStepProps = {
  /** The show style this step made or adopted; null before. */
  readonly done: { readonly name: string } | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onCreate: (name: string) => void;
};

export function ShowStyleStep({ done, pending, error, onCreate }: ShowStyleStepProps) {
  const [name, setName] = useState('News');
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() !== '' && !pending) onCreate(name.trim());
  };
  return (
    <div className="grid gap-4" data-testid="onboarding-show-style">
      <p className="text-sm text-muted-foreground">
        A show style is your show's grammar: the piece types (camera, VT, lower third, full screen), the layers they play on, and the adlib panels. Start from the template; edit it later under Show styles.
      </p>
      {done !== null ? (
        <Done>Show style “{done.name}” is ready.</Done>
      ) : (
        <form className="grid gap-4" onSubmit={submit}>
          <Field invalid={error !== null}>
            <Label htmlFor="onboarding-show-style-name">Name</Label>
            <Input id="onboarding-show-style-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} maxLength={200} />
            <FieldError>{error}</FieldError>
          </Field>
          <Button type="submit" className="justify-self-start" disabled={pending || name.trim() === ''} data-testid="onboarding-create-show-style">
            {pending ? 'Creating…' : 'Create from the template'}
          </Button>
        </form>
      )}
    </div>
  );
}

// ─── 2. studio ───

export type StudioRequest = { readonly name: string; readonly showStyleId: string | null; readonly host: string; readonly basePort: number };

export type StudioStepProps = {
  readonly showStyles: readonly { readonly id: string; readonly name: string }[];
  /** The show style to pick first (the one step 1 made). */
  readonly defaultShowStyleId: string | null;
  readonly defaultHost: string;
  readonly defaultBasePort: number;
  readonly done: { readonly name: string } | null;
  /** The pair's judgement once the studio exists (`validateShowStyle`): null before, or while it loads. */
  readonly validation: { readonly valid: boolean; readonly problems: readonly string[] } | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onCreate: (request: StudioRequest) => void;
};

export function StudioStep({ showStyles, defaultShowStyleId, defaultHost, defaultBasePort, done, validation, pending, error, onCreate }: StudioStepProps) {
  const [name, setName] = useState('Studio A');
  const [showStyleId, setShowStyleId] = useState(defaultShowStyleId ?? showStyles[0]?.id ?? '');
  const [host, setHost] = useState(defaultHost);
  const [port, setPort] = useState(String(defaultBasePort));
  const basePort = Number(port);
  const portValid = Number.isInteger(basePort) && basePort > 0 && basePort < 65_530;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim() === '' || host.trim() === '' || !portValid || pending) return;
    onCreate({ name: name.trim(), showStyleId: showStyleId === '' ? null : showStyleId, host: host.trim(), basePort });
  };
  return (
    <div className="grid gap-4" data-testid="onboarding-studio">
      <p className="text-sm text-muted-foreground">
        A studio is the rig: its devices (vision mixer, video server, graphics) and which of the show style's layers each one plays. The template has one of each, pointed at the simulated devices; change the addresses to your own under Studios.
      </p>
      {done !== null ? (
        <>
          <Done>Studio “{done.name}” is ready.</Done>
          {validation === null ? null : validation.valid ? (
            <p className="text-sm" data-testid="onboarding-pair-valid">
              The show style and the studio fit: every layer is mapped to a device that can play it.
            </p>
          ) : (
            <div className="grid gap-1 text-sm" data-testid="onboarding-pair-problems">
              <p className="text-destructive">The show style and the studio do not fit yet:</p>
              <ul className="list-disc pl-5">
                {validation.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : (
        <form className="grid gap-4" onSubmit={submit}>
          <Field>
            <Label htmlFor="onboarding-studio-name">Name</Label>
            <Input id="onboarding-studio-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} maxLength={200} />
          </Field>
          <Field>
            <Label htmlFor="onboarding-studio-show-style">Show style</Label>
            <NativeSelect id="onboarding-studio-show-style" value={showStyleId} onChange={(e) => setShowStyleId(e.target.value)} disabled={pending}>
              <option value="">None yet</option>
              {showStyles.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label htmlFor="onboarding-studio-host">Devices’ host</Label>
              <Input id="onboarding-studio-host" value={host} onChange={(e) => setHost(e.target.value)} disabled={pending} />
            </Field>
            <Field invalid={!portValid}>
              <Label htmlFor="onboarding-studio-port">First port</Label>
              <Input id="onboarding-studio-port" type="number" value={port} onChange={(e) => setPort(e.target.value)} disabled={pending} />
              <FieldDescription>The start of the devices’ port block: the template’s mixer, video server and graphics listen on it +5, +6 and +7.</FieldDescription>
            </Field>
          </div>
          <FieldError>{error}</FieldError>
          <Button type="submit" className="justify-self-start" disabled={pending || name.trim() === '' || !portValid} data-testid="onboarding-create-studio">
            {pending ? 'Creating…' : 'Create from the template'}
          </Button>
        </form>
      )}
    </div>
  );
}

// ─── 3. a server, or the cloud ───

export type ServerStepProps = {
  readonly choice: 'server' | 'cloud' | null;
  /** Adding a server needs the cloud (a node does not enrol nodes): false shows why. */
  readonly canAddServer: boolean;
  /** The site made and its fresh code: the install line to run. Null before. */
  readonly enrolment: { readonly siteName: string; readonly dockerRun: string; readonly expiresAt: string } | null;
  readonly done: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onChoose: (choice: 'server' | 'cloud') => void;
  readonly onCreateSite: (name: string) => void;
  readonly onCopy: (text: string) => void;
  readonly onOpenSites: () => void;
  readonly onOpenCloud: () => void;
  readonly onDone: () => void;
};

function ServerChoice({ onChoose }: Pick<ServerStepProps, 'onChoose'>) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <button type="button" className="grid gap-1 rounded-md border p-4 text-left transition-colors hover:bg-accent" onClick={() => onChoose('server')} data-testid="onboarding-choose-server">
        <span className="flex items-center gap-2 font-medium">
          <ServerIcon className="size-4" aria-hidden="true" />
          Add a server
        </span>
        <span className="text-sm text-muted-foreground">Run ACP on your own machine in the studio (Docker). Add a second one later for main/backup.</span>
      </button>
      <button type="button" className="grid gap-1 rounded-md border p-4 text-left transition-colors hover:bg-accent" onClick={() => onChoose('cloud')} data-testid="onboarding-choose-cloud">
        <span className="flex items-center gap-2 font-medium">
          <CloudIcon className="size-4" aria-hidden="true" />
          Launch in the cloud
        </span>
        <span className="text-sm text-muted-foreground">No hardware: a production runs in our cloud and reaches your devices over the internet.</span>
      </button>
    </div>
  );
}

function AddServer({ canAddServer, enrolment, pending, error, onCreateSite, onCopy, onOpenSites, onDone }: Omit<ServerStepProps, 'choice' | 'done' | 'onChoose' | 'onOpenCloud'>) {
  const [name, setName] = useState('Main studio');
  if (!canAddServer) {
    return <p className="text-sm text-muted-foreground">Servers are added from the cloud portal: open it there to get an install line.</p>;
  }
  if (enrolment !== null) {
    return (
      <div className="grid gap-3" data-testid="onboarding-enrolment">
        <p className="text-sm">
          Site “{enrolment.siteName}” is ready. Run this on the server (Docker, host networking); the code in it works once, until {enrolment.expiresAt.replace('T', ' ').slice(0, 16)} UTC:
        </p>
        <pre className="overflow-x-auto rounded-md border bg-muted p-3 font-mono text-xs" data-testid="onboarding-docker-run">
          {enrolment.dockerRun}
        </pre>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => onCopy(enrolment.dockerRun)}>
            <CopyIcon />
            Copy
          </Button>
          <Button variant="outline" onClick={onOpenSites}>
            Open Sites
          </Button>
          <Button onClick={onDone} data-testid="onboarding-server-done">
            Done
          </Button>
        </div>
      </div>
    );
  }
  return (
    <form
      className="grid gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() !== '' && !pending) onCreateSite(name.trim());
      }}
    >
      <Field invalid={error !== null}>
        <Label htmlFor="onboarding-site-name">Site name</Label>
        <Input id="onboarding-site-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} maxLength={100} />
        <FieldDescription>Where the server stands. A site holds a main and a backup server.</FieldDescription>
        <FieldError>{error}</FieldError>
      </Field>
      <Button type="submit" className="justify-self-start" disabled={pending || name.trim() === ''} data-testid="onboarding-create-site">
        {pending ? 'Creating…' : 'Create the site and an install line'}
      </Button>
    </form>
  );
}

export function ServerStep(props: ServerStepProps) {
  const { choice, done, onChoose, onOpenCloud } = props;
  return (
    <div className="grid gap-4" data-testid="onboarding-server">
      {done ? <Done>{choice === 'cloud' ? 'You chose the cloud: launch a production from the Cloud page.' : 'Your server is on its way: it appears under Sites once it has enrolled.'}</Done> : null}
      {!done && choice === null ? <ServerChoice onChoose={onChoose} /> : null}
      {!done && choice === 'server' ? <AddServer {...props} /> : null}
      {!done && choice === 'cloud' ? (
        <div className="grid gap-3" data-testid="onboarding-cloud">
          <p className="text-sm">
            A cloud production runs a studio of your automatic <strong>cloud</strong> site. Create one under Studios with the site “cloud” (or pick this studio’s devices’ public addresses), then launch from the Cloud page.
          </p>
          <div className="flex gap-2">
            <Button onClick={onOpenCloud} data-testid="onboarding-open-cloud">
              Open the Cloud page
            </Button>
            <Button variant="outline" onClick={props.onDone}>
              Done
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── 4. a first rundown ───

export type RundownStepProps = {
  readonly done: { readonly id: string; readonly name: string } | null;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onCreate: (name: string, seed: boolean) => void;
  readonly onOpen: (rundownId: string) => void;
};

export function RundownStep({ done, pending, error, onCreate, onOpen }: RundownStepProps) {
  const [name, setName] = useState('First show');
  const [seed, setSeed] = useState(true);
  return (
    <div className="grid gap-4" data-testid="onboarding-rundown">
      <p className="text-sm text-muted-foreground">A rundown is the running order of a show: segments, parts, and the pieces each part plays.</p>
      {done !== null ? (
        <div className="grid gap-3">
          <Done>Rundown “{done.name}” is ready.</Done>
          <Button className="justify-self-start" onClick={() => onOpen(done.id)} data-testid="onboarding-open-rundown">
            Open it
          </Button>
        </div>
      ) : (
        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (name.trim() !== '' && !pending) onCreate(name.trim(), seed);
          }}
        >
          <Field invalid={error !== null}>
            <Label htmlFor="onboarding-rundown-name">Name</Label>
            <Input id="onboarding-rundown-name" value={name} onChange={(e) => setName(e.target.value)} disabled={pending} maxLength={500} />
            <FieldError>{error}</FieldError>
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-primary" checked={seed} onChange={(e) => setSeed(e.target.checked)} disabled={pending} data-testid="onboarding-seed" />
            Fill it with a demo show for the template (segments, parts, pieces)
          </label>
          <Button type="submit" className="justify-self-start" disabled={pending || name.trim() === ''} data-testid="onboarding-create-rundown">
            {pending ? 'Creating…' : 'Create the rundown'}
          </Button>
        </form>
      )}
    </div>
  );
}
